'use client';

import type { AnimationItem } from 'lottie-web';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { getAssetUrl } from 'lib/config/assetUrls';
import { useLottieAnimation } from 'hooks/animation/useLottieAnimation';
import { useTranslations } from 'next-intl';
import styles from './HealthTracker.module.css';
import { withPlayfairO } from './StyleConstants';

const UHX_ROTATE_LOTTIE = getAssetUrl(
  '/web_v2/site-static/lottie/ultrahumanX-rotate.json',
);

interface CardData {
  headKey: string;
  descKey: string;
}

const HealthTrackerCard = ({ data }: { data?: CardData }) => {
  const t = useTranslations('ring');

  return (
    <div className={styles.healthTrackerCard}>
      {data && (
          <>
            <h3>{t(data.headKey)}</h3>
            <p className="desc">{t(data.descKey)}</p>
          </>
        )}
      </div>
    );
};

interface HealthTrackerItems {
  colA?: string | null;
  colB?: string | CardData;
  colC?: string | CardData;
}
interface HealthTrackerProps {
  head?: string;
  image?: string | boolean;
  noMargin?: boolean;
  items?: HealthTrackerItems[];
}

export const HealthTracker = ({
  content,
  fullBg = false,
  id,
}: {
  content?: HealthTrackerProps;
  fullBg?: boolean;
  id?: string;
}) => {
    const pathname = usePathname();
    const t = useTranslations('ring');
    const richTags = {
      br: () => <br />,
    };
    const animContainerRef = useRef<HTMLDivElement>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const lottieRef = useRef<AnimationItem | null>(null);

    const [lottieReady, setLottieReady] = useState(false);

    useLottieAnimation(animContainerRef, {
      path: UHX_ROTATE_LOTTIE,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      onInstance: (animation: AnimationItem) => {
        lottieRef.current = animation;
        setLottieReady(true);
      },
    });

    const [lottieInView, setLottieInView] = useState(false);

    useEffect(() => {
      const el = animContainerRef.current;
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => setLottieInView(!!entries[0]?.isIntersecting),
        { rootMargin: '200px' },
      );
      io.observe(el);
      return () => io.disconnect();
    }, []);

    useEffect(() => {
      const anim = lottieRef.current;
      if (!anim) return;
      if (lottieInView) {
        if (anim.isPaused) anim.play();
      } else if (!anim.isPaused) {
        anim.pause();
      }
    }, [lottieInView, lottieReady]);

    useEffect(() => {
      if (id && window.location.hash.includes(`#${id}`)) {
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      }
    }, [id, pathname]);

    return (
      <section ref={sectionRef} className={`${styles.root} ${fullBg ? 'full-bg' : ''}`} id={id}>
        <div className="root-content">
          <h2 className="head">
            {content?.head
              ? withPlayfairO(t.rich(content.head, richTags))
              : null}
          </h2>
          {content?.image ? (
            <div className="lottie-container" ref={animContainerRef}></div>
          ) : null}

          <div className="content">
            {content?.items &&
              content?.items?.map((item, index) => (
                <div className="row" key={index}>
                  <p className="col">{item?.colA}</p>
                  {typeof item?.colB === 'object' &&
                  item?.colB !== null &&
                  item?.colB &&
                  'headKey' in item.colB &&
                  'descKey' in item.colB ? (
                    <div className="col card">
                      <HealthTrackerCard data={item?.colB as CardData} />
                    </div>
                  ) : (
                    <p className="col">
                      {item?.colB
                        ? t.rich(item.colB as string, richTags)
                        : null}
                    </p>
                  )}
                  {typeof item?.colC === 'object' &&
                  item?.colC !== null &&
                  item?.colC &&
                  'headKey' in item.colC &&
                  'descKey' in item.colC ? (
                    <div className="col card">
                      <HealthTrackerCard data={item?.colC as CardData} />
                    </div>
                  ) : (
                    <p className="col">
                      {item?.colC
                        ? t.rich(item.colC as string, richTags)
                        : null}
                    </p>
                  )}
                </div>
              ))}
          </div>
        </div>
      </section>
    );
};
